import React, { useState } from 'react';
import { TrendingUp, Target, Shield, Users } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const HomepageContent = () => {
  const [expanded, setExpanded] = useState(false);

  const features = [
    {
      icon: <TrendingUp className="w-8 h-8 text-primary-600" />,
      title: "Data-Driven Insights",
      description: "Our recommendations are rooted in rigorous analysis of market data, technical indicators, and fundamental reports for reliable insights."
    },
    {
      icon: <Target className="w-8 h-8 text-primary-600" />,
      title: "Personalized Guidance",
      description: "Tailored strategies based on your unique financial goals, risk tolerance, and investment timeline, avoiding generic solutions."
    },
    {
      icon: <Shield className="w-8 h-8 text-primary-600" />,
      title: "Ethical Transparency",
      description: "As a SEBI registered analyst, we provide transparent advice with full disclosure of risks, adhering to strict ethical standards."
    },
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      title: "Comprehensive Research",
      description: "Expert analysis across equity, commodity markets, and technical strategies to empower confident investment decisions."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-left mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-snug tracking-tight border-l-4 border-primary-600 pl-4">
            Your Partner in Financial Growth: A SEBI Registered Research Analyst You Can Trust
          </h2>
          <div className="relative">
            <div
              id="homepage-content-readmore"
              className={`text-base md:text-lg text-gray-700 ${expanded ? 'max-w-none w-full prose prose-lg prose-gray' : 'max-w-3xl'} `}
              style={!expanded ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
            >
              <p className="mb-4">
                Welcome to Omkar Research Analyst. I’m Jignesh Patel, a NISM Certified Research Analyst, dedicated to being your trusted partner in navigating the complexities of financial markets. Wealth creation can feel overwhelming with market jargon, news, and unregulated advice. That’s why I founded Omkar Research Analyst—to simplify the journey and empower you with credible, data-driven guidance.
                {expanded && (
                  <div className="space-y-4 mt-4">
                    <p>
                      At Omkar Research Analyst, we reject speculative "hot tips" in favor of meticulous, research-backed strategies. As a SEBI registered research analyst (INH000021359), we pride ourselves on delivering transparent, ethical, and personalized advice tailored to your financial goals. Our mission is to equip you with the knowledge and confidence to make informed investment decisions, grounded in rigorous analysis and a commitment to your success.
                    </p>
                    <p>
                      <strong>Our Mission & Approach:</strong> Founded with a vision to demystify financial markets, Omkar Research Analyst combines deep market expertise, cutting-edge analytical tools, and a client-first philosophy. Our core principles include:
                    </p>
                    <ul className="list-disc list-inside mb-4">
                      <li><strong>Data-Driven Insights:</strong> Our recommendations are rooted in comprehensive analysis of market data, technical indicators, and fundamental reports, ensuring clarity and reliability.</li>
                      <li><strong>Transparent & Ethical Advisory:</strong> We operate with full transparency, disclosing risks and rationales behind every recommendation, with a clear and upfront fee structure.</li>
                      <li><strong>Personalized Guidance:</strong> Your financial journey is unique. We craft strategies based on your goals, risk tolerance, and timeline, avoiding generic solutions.</li>
                    </ul>
                    <p>
                      <strong>Our Services:</strong> As a SEBI registered research analyst, we offer a comprehensive suite of services, including:
                    </p>
                    <ul className="list-disc list-inside mb-4">
                      <li><strong>Equity Market Research:</strong> From long-term stock picks with strong fundamentals to short-term trading opportunities, we provide detailed reports with price targets and risk assessments.</li>
                      <li><strong>Commodity Market Research:</strong> Expert trading calls on MCX for gold, silver, crude oil, and energy markets, with clear stop-loss and target levels for risk management.</li>
                      <li><strong>Technical Analysis & Risk Management:</strong> Using advanced charting and pattern recognition, we identify optimal entry and exit points while prioritizing risk mitigation through diversification and stop-loss strategies.</li>
                    </ul>
                    <p>
                      <strong>Our Commitment to SEBI Compliance:</strong> Our SEBI registration is more than a credential—it’s a promise of integrity. It ensures verified expertise, accountability, and protection from misleading claims. We never guarantee returns, adhering strictly to SEBI’s ethical guidelines.
                    </p>
                    <p>
                      <strong>Meet Jignesh Patel:</strong> As a NISM Certified Research Analyst and founder, I am driven by a passion to empower investors with unbiased, high-quality research. My team and I are committed to guiding both beginners and seasoned investors toward financial success.
                    </p>
                    <p>
                      <strong>Our Track Record:</strong> With years of market experience, we focus on serving clients across India with research-backed, risk-managed recommendations. Our 24/7 market support ensures you’re never alone in your investment journey.
                    </p>
                    <p>
                      <strong>Ready to Start?</strong> Whether you’re new to investing or a seasoned trader, Omkar Research Analyst is here to guide you. Contact us at <a href="mailto:info@omkarresearch.com" className="text-primary-600 hover:text-primary-700 hover:underline">info@omkarresearch.com</a> or call <a href="tel:9898001671" className="text-primary-600 hover:text-primary-700 hover:underline">9898001671</a> to begin your journey to financial success.
                    </p>
                    <p className="text-sm text-gray-500">
                      <strong>Disclaimer:</strong> The information and data provided on this website are for educational and illustrative purposes only. All investments in the securities market are subject to market risks. Read all related documents carefully before investing. Registration granted by SEBI, enlistment as RA with Exchange, and certification from NISM do not guarantee performance of the intermediary or provide any assurance of returns to investors. The views expressed here are based on independent analysis and do not guarantee any returns or profits.
                    </p>
                  </div>
                )}
              </p>
            </div>
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center px-6 py-3 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
              aria-expanded={expanded}
              aria-controls="homepage-content-readmore"
            >
              {expanded ? 'Read Less' : 'Read More'}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="text-center p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="flex justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-700 max-w-4xl mx-auto">
            Join investors who trust Omkar Research Analyst to navigate the complexities of the stock and commodity markets. With our SEBI registered expertise, personalized strategies, and unwavering commitment to your success, we’re here to help you achieve your financial goals.
          </p>
          <NavLink
            to="/contactus"
            className="mt-6 inline-flex items-center px-6 py-3 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
          >
            Get in Touch
          </NavLink>
        </div>
      </div>
    </section>
  );
};

export default HomepageContent;